import {
    type CreateFamilyInput,
    type Family,
    FAMILIES_PATH,
    familyPath,
    familySchema,
    type UpdateFamilyInput,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

export function useFamily(familyId: string | undefined): UseQueryResult<Family> {
    return useQuery({
        queryKey: queryKeys.family(familyId ?? ''),
        enabled: familyId !== undefined,
        queryFn: ({ signal }) => apiGet(familyPath(familyId ?? ''), familySchema, { signal }),
    });
}

export function useCreateFamily(): UseMutationResult<Family, Error, CreateFamilyInput> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateFamilyInput) =>
            apiRequest('POST', FAMILIES_PATH, familySchema, { body: input }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        },
    });
}

export function useUpdateFamily(
    familyId: string,
): UseMutationResult<Family, Error, UpdateFamilyInput> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: UpdateFamilyInput) =>
            apiRequest('PATCH', familyPath(familyId), familySchema, { body: input }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.family(familyId) });
            await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        },
    });
}
